// Equity Curve Component - Visual bankroll chart

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { useTradingStore } from '../lib/store';

export function EquityCurve() {
  const strategyA = useTradingStore((state) => state.strategyA);
  const strategyB = useTradingStore((state) => state.strategyB);
  
  // Build equity curve data
  const dataA = buildEquityCurve(strategyA.initialBankroll, strategyA.trades);
  const dataB = buildEquityCurve(strategyB.initialBankroll, strategyB.trades);
  
  // Merge the data for charting
  const maxLength = Math.max(dataA.length, dataB.length);
  const chartData = [];
  
  for (let i = 0; i < maxLength; i++) {
    chartData.push({
      trade: i,
      strategyA: dataA[i]?.bankroll ?? dataA[dataA.length - 1]?.bankroll ?? strategyA.initialBankroll,
      strategyB: dataB[i]?.bankroll ?? dataB[dataB.length - 1]?.bankroll ?? strategyB.initialBankroll,
    });
  }
  
  // If no trades, show placeholder
  if (chartData.length <= 1) {
    return (
      <div className="equity-curve empty">
        <h3>📈 Equity Curve</h3>
        <div className="chart-placeholder">
          <p>Chart will appear once trades are executed</p>
          <div className="placeholder-stats">
            <div className="stat">
              <span className="label">Strategy A</span>
              <span className="value">${strategyA.bankroll.toFixed(2)}</span>
            </div>
            <div className="stat">
              <span className="label">Strategy B</span>
              <span className="value">${strategyB.bankroll.toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  const initialValue = Math.max(strategyA.initialBankroll, strategyB.initialBankroll);
  
  return (
    <div className="equity-curve">
      <h3>📈 Equity Curve</h3>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#333" />
          <XAxis 
            dataKey="trade" 
            stroke="#888"
            label={{ value: 'Trade #', position: 'insideBottom', offset: -5 }}
          />
          <YAxis 
            stroke="#888"
            domain={['auto', 'auto']}
            tickFormatter={(value) => `$${value.toFixed(2)}`}
          />
          <Tooltip 
            contentStyle={{ background: '#1a1a2e', border: '1px solid #333' }}
            formatter={(value: number) => [`$${value.toFixed(2)}`, '']}
          />
          <Legend />
          <ReferenceLine 
            y={initialValue} 
            stroke="#666" 
            strokeDasharray="5 5"
            label={{ value: 'Start', position: 'right', fill: '#666' }}
          />
          <Line
            type="monotone"
            dataKey="strategyA"
            stroke="#3498db"
            strokeWidth={2}
            name="Strategy A (Contrarian)"
            dot={false}
            activeDot={{ r: 6 }}
          />
          <Line
            type="monotone"
            dataKey="strategyB"
            stroke="#2ecc71"
            strokeWidth={2}
            name="Strategy B (Value)"
            dot={false}
            activeDot={{ r: 6 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

interface EquityPoint {
  trade: number;
  bankroll: number;
}

function buildEquityCurve(initialBankroll: number, trades: any[]): EquityPoint[] {
  const resolvedTrades = trades.filter(t => t.result !== 'pending');
  
  if (resolvedTrades.length === 0) {
    return [{ trade: 0, bankroll: initialBankroll }];
  }
  
  const curve: EquityPoint[] = [{ trade: 0, bankroll: initialBankroll }];
  let currentBankroll = initialBankroll;
  
  resolvedTrades.forEach((trade, index) => {
    currentBankroll += trade.pnl || 0;
    curve.push({
      trade: index + 1,
      bankroll: currentBankroll,
    });
  });
  
  return curve;
}
